import { createSelector } from "@reduxjs/toolkit";
import transFund from "redux/reducers/buy-sell-switching/transaction-fund"
import switchFund from "redux/reducers/buy-sell-switching/switch-fund"
import concat from 'lodash/concat'
import find from 'lodash/find'
import map from 'lodash/map'
import uniqBy from 'lodash/uniqBy'
import flatMap from 'lodash/flatMap'

export const BuyFundSelector = {
  state: (state) => state.ultrahigh.pwm.buySellSwitching.buy,
  currentPageDirection: createSelector(
    [
      (state) => BuyFundSelector.state(state).page,
      (state) => BuyFundSelector.state(state).direction,
    ],
    (page, direction) => ({ page, direction })),
  submitting: state => BuyFundSelector.state(state).submitting,
  sendingOTP: state => BuyFundSelector.state(state).sendingOTP,
  otp: state => BuyFundSelector.state(state).otp,
  resultTransaction: state => BuyFundSelector.state(state).resultTransaction,
  initializing: state => BuyFundSelector.state(state).initializing,
  redeemAccount: state => BuyFundSelector.state(state).redeemAccount,
  riskUserProfileLevel: state => BuyFundSelector.state(state).riskUserProfileLevel,
  exchangeRateFlag: state => BuyFundSelector.state(state).exchangeRateFlag,
  cutOffInfo: state => BuyFundSelector.state(state).cutOffInfo,
}

export const SellFundSelector = {
  state: (state) => state.ultrahigh.pwm.buySellSwitching.sell,
  currentPageDirection: createSelector(
    [
      (state) => SellFundSelector.state(state).page,
      (state) => SellFundSelector.state(state).direction,
    ],
    (page, direction) => ({ page, direction })),
  submitting: state => SellFundSelector.state(state).submitting,
  sendingOTP: state => SellFundSelector.state(state).sendingOTP,
  otp: state => SellFundSelector.state(state).otp,
  resultTransaction: state => SellFundSelector.state(state).resultTransaction,
  assetKcusPending: state => SellFundSelector.state(state).assetKcus.pending,
  assetKcus: state => SellFundSelector.state(state).assetKcus.data,
  fetchedAssetKcus: state => SellFundSelector.state(state).assetKcus.done,
  initializing: state => SellFundSelector.state(state).initializing,
  redeemAccount: state => SellFundSelector.state(state).redeemAccount,
  cutOffInfo: state => SellFundSelector.state(state).cutOffInfo,
}

export const SwitchFundSelector = {
  state: (state) => state.ultrahigh.pwm.buySellSwitching[switchFund.name],
  currentPageDirection: createSelector(
    [
      (state) => SwitchFundSelector.state(state).page,
      (state) => SwitchFundSelector.state(state).direction,
    ],
    (page, direction) => ({ page, direction })),
  submitting: state => SwitchFundSelector.state(state).submitting,
  sendingOTP: state => SwitchFundSelector.state(state).sendingOTP,
  otp: state => SwitchFundSelector.state(state).otp,
  resultTransaction: state => SwitchFundSelector.state(state).resultTransaction,
  assetKcusPending: state => SwitchFundSelector.state(state).assetKcus.pending,
  assetKcus: state => SwitchFundSelector.state(state).assetKcus.data,
  fetchedAssetKcus: state => SwitchFundSelector.state(state).assetKcus.done,
  availableFundsByAssetKcus: createSelector(
    [state => SwitchFundSelector.assetKcus(state)],
    assetKcus => flatMap(Object.entries(assetKcus), ([, value]) => map(value.funds, 'fundId')),
  ),
  availableSwitchFundsTemp: state => SwitchFundSelector.state(state).availableSwitchFundsTemp,
  availableSwitchFundsState: createSelector(
    [
      state => SwitchFundSelector.availableSwitchFundsTemp(state),
      state => SwitchFundSelector.state(state).fromFundId,
    ],
    (availableSwitchFundsTemp, fundId) => {
      const state = availableSwitchFundsTemp[fundId]
      return {
        pending: !!state?.pending,
        funds: map(state?.funds, 'fundId') ?? [],
      }
    },
  ),
  riskUserProfileLevel: state => SwitchFundSelector.state(state).riskUserProfileLevel,
  exchangeRateFlag: state => SwitchFundSelector.state(state).exchangeRateFlag,
  cutOffInfo: state => SwitchFundSelector.state(state).cutOffInfo,
}

export const TransactionFundSelector = {
  state: (state) => state.ultrahigh.pwm.buySellSwitching[transFund.name],
  loading: (state) => TransactionFundSelector.state(state).loading,
  canceled: (state) => TransactionFundSelector.state(state).canceled,
  uhmfSelected: (state) => TransactionFundSelector.state(state).uhmfSelected,
  activeTabKey: (state) => TransactionFundSelector.state(state).activeTabKey,
  holdingTrans: (state) => TransactionFundSelector.state(state).holdingTrans,
  retrospectiveTrans: (state) => TransactionFundSelector.state(state).retrospectiveTrans,
  transactionType: (state) => TransactionFundSelector.state(state).transactionType,
  dateFilter: (state) => TransactionFundSelector.state(state).dateFilter,
  statusFilter: (state) => TransactionFundSelector.state(state).statusFilter,
  viewedTransactionDetail: createSelector(
    [
      state => TransactionFundSelector.holdingTrans(state),
      state => TransactionFundSelector.retrospectiveTrans(state),
      state => TransactionFundSelector.state(state).transactionNoSelected,
    ],
    (holdingTrans, retrospectiveTrans, transactionNo) => {
      const all = concat(
        holdingTrans.histories,
        retrospectiveTrans.allData.transaction,
        retrospectiveTrans.buyData.transaction,
        retrospectiveTrans.sellData.transaction,
        retrospectiveTrans.switchData.transaction,
      )
      return find(uniqBy(all, 'transactionNo'), { transactionNo })
    }
  ),
}